import { useState, useEffect } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { Player } from "@lottiefiles/react-lottie-player";
import loadingLogo from "../../theme/logo/logo_loading_icon_only.json";

const breathe = keyframes`
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LONG_WAIT_SECONDS = 5;

const ComponentLoading = ({
  message = "Just a moment, we're getting things ready for you…",
  size = "medium", // small, medium, large
  fullHeight = false, // whether to take full height of container
}) => {
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(
      () => setShowLongWaitMessage(true),
      LONG_WAIT_SECONDS * 1000,
    );
    return () => clearTimeout(t);
  }, [message]);

  const sizeMap = {
    small: 200,
    medium: 300,
    large: 400,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: fullHeight ? "100%" : "auto",
        minHeight: fullHeight ? "100%" : { xs: "100px", sm: "150px" },
        p: { xs: 1.5, sm: 2 },
        width: "100%",
      }}
      role='status'
      aria-live='polite'
      aria-label={message ? `${message}, loading` : "Loading content"}
    >
      <Player
        autoplay
        loop
        src={loadingLogo}
        style={{
          height: `${sizeMap[size]}px`,
          width: `${sizeMap[size]}px`,
          maxWidth: "100%",
          maxHeight: "400px",
        }}
        aria-label='Animated loading indicator'
      />
      {message && (
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{
            fontWeight: 500,
            letterSpacing: message?.match(/^[a-zA-Z\s]*$/) ? 1 : 0.5,
            animation: `${breathe} 3s ease-in-out infinite`,
            textAlign: "center",
            textShadow: "0 1px 4px rgba(0,0,0,0.06)",
            maxWidth: "80%",
            fontSize: "1.2rem",
          }}
        >
          {message}
        </Typography>
      )}
      {message && showLongWaitMessage && (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            mt: 1.5,
            fontWeight: 500,
            textAlign: "center",
            maxWidth: "80%",
            fontStyle: "italic",
            opacity: 0, // 初始隐藏（由动画控制）
            animation: `${fadeIn} 0.6s ease-out forwards`,
            animationDelay: "0.2s", // 稍微错开主文案
          }}
        >
          Taking a little extra time to ensure accuracy. We appreciate your
          patience.
        </Typography>
      )}
    </Box>
  );
};

export default ComponentLoading;
